import { BOT_PERSONALITIES } from '@src/constants/Domain';
import { GOOGLE_ANALYTICS } from '@src/const';
import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import { DEFAULT_LANGUAGE } from '../../../constants/MogamboConstants';

const STATUS_ACTIVE = { title: 'Active', value: true };
const STATUS_INACTIVE = { title: 'Inactive', value: true };
const DEFAULT_ADVANCED_PROPERTIES = {
  active: { title: 'Active', value: true },
  gender: 'Male',
  companyName: 'Haptik',
};

export function generateThresholdData(data) {
  const thresholds = [
    {
      name: 'Sensim V1',
      status: data.sensim_v1_state,
      priority: data.sensim_priority.indexOf('sensim_v1') + 1,
      threshold: data.sensim_v1_threshold,
      flag: 'ON',
      classifier: JSON.stringify(data.classifier_JSON),
    },
    {
      name: 'Sensim V2',
      status: data.sensim_v2_state,
      priority: data.sensim_priority.indexOf('sensim_v2') + 1,
      threshold: data.sensim_v2_threshold,
      flag: 'OFF',
      classifier: '',
    },
    {
      name: 'Sensim V3',
      status: data.sensim_v3_state,
      priority: data.sensim_priority.indexOf('sensim_v3') + 1,
      threshold: data.sensim_v3_threshold,
      flag: 'OFF',
      classifier: '',
    },
    {
      name: 'Global Sensim Threshold',
      status: true,
      priority: 5,
      threshold: data.global_threshold,
      flag: 'NA',
      classifier: '',
    },
    {
      name: 'Global Sensim Diff Threshold',
      status: true,
      priority: 5,
      threshold: data.global_diff_threshold,
      flag: 'NA',
      classifier: '',
    },
  ];

  thresholds.sort((a, b) => {
    return a.priority - b.priority;
  });

  return thresholds;
}

export function getBotMetadata(languageList = []) {
  const personalities = Object.keys(BOT_PERSONALITIES).map((personaKey, i) => ({
    title: BOT_PERSONALITIES[personaKey],
    key: i,
  }));

  return {
    personalities,
    languages: languageList,
  };
}

export function getBotData({
  botId,
  basicProperties,
  advancedProperties,
  integration_config,
  onError,
  editMode,
  fallbackAdvancedProperties = DEFAULT_ADVANCED_PROPERTIES,
}) {
  const basicPropertiesValid = basicProperties.validate();
  const advancedPropertiesValid = advancedProperties
    ? advancedProperties.validate()
    : true;
  if (!basicPropertiesValid || !advancedPropertiesValid) {
    onError(
      'Invalid Bot Properties Detected! Please review the highlighted fields in red.'
    );
    return null;
  }

  const {
    chatflowName,
    description,
    testBusiness,
    testPartner,
    language,
    enableHinglish,
  } = basicProperties.state;

  const {
    active,
    gender,
    companyName,
    appName,
    feedbackSmartActionLink,
    companyWebsite,
    botLocation,
    botPersona,
    companyContext,
    contextEnhancementInstr,
    intentDetectionInstr,
    answeringInstr,
    aiAgentAuthEnabled,
    aiAgentSessionTimeout,
    aiAgentSessionTimeoutEnabled,
    addDateAndTimeInPrompt,
    detectLanguageForAllMessages,
    aiAgentVoiceEnabled,
    aIAgentVoiceModel,
    aiAgentTtsEnabled,
    aiAgentPhoneNumber,
    aiAgentVoiceForOutput,
    aiAgentGreetingMessage,
    aiAgentVoiceProvider,
    botContext,
    aiAgentVoiceLocale,
  } = advancedProperties
    ? advancedProperties.state
    : fallbackAdvancedProperties;

  /**
   * Since Domain name is not to be rendered on UI,
   * chatflowName will have same rules as domainName and
   * same will be passed to domain_name
   *
   * --------
   *
   * When Editing a bot, domain_name and via_name should not be changed.
   * Hence, added editMode param
   *
   * --------
   *
   * Formatting of via_name is same as it exists currently.
   * eg. If chatflowName is entered as 'test_123',
   * via_name would be formatted as 'test123channel'
   */
  const newBot = {
    domain_name: editMode
      ? basicProperties.state.domainName.toLowerCase()
      : chatflowName.toLowerCase(),
    partner_name: testPartner.partner_name,
    business_name: testBusiness.toLowerCase(),
    via_name: editMode
      ? basicProperties.state.business_via_name.toLowerCase()
      : `${testBusiness
          .toLowerCase()
          .replace(/_/g, '')
          .replace(/ /g, '')}channel`,
    active: active.value,
    description,
    integration_config,
    ai_agent_auth_enabled: aiAgentAuthEnabled,
    detect_language_for_all_messages: detectLanguageForAllMessages,
    ai_agent_session_timeout: aiAgentSessionTimeout,
    ai_agent_session_timeout_enabled: aiAgentSessionTimeoutEnabled,
    add_date_and_time_in_prompt: addDateAndTimeInPrompt,
    voice_enabled: aiAgentVoiceEnabled,
    voice_model_id: aIAgentVoiceModel?.id,
    tts_enabled: aiAgentTtsEnabled,
    phone_number: aiAgentPhoneNumber,
    voice_config: {
      voice_id: aiAgentVoiceForOutput?.name,
      greeting_msg: aiAgentGreetingMessage,
      provider: aiAgentVoiceProvider,
      locale: aiAgentVoiceLocale?.id,
    },
    bot_properties: {
      bot_context: botContext,
      company_context: companyContext,
      context_enhancement_instr: contextEnhancementInstr,
      intent_detection_instr: intentDetectionInstr,
      answering_instr: answeringInstr,
      persona: botPersona,
      reply_tags: {
        [language?.code || DEFAULT_LANGUAGE]: {
          '%appname': appName,
          '%botgender': gender,
          '%botname': chatflowName,
          '%company': companyName,
          '%feedbacklink': feedbackSmartActionLink,
          '%location': botLocation,
          '%sitelink': companyWebsite,
        },
      },
    },
    hybrid_languages: {
      hinglish: enableHinglish,
    },
  };

  if (botId) {
    newBot.domain_id = botId;
  }
  return newBot;
}

export function getBotProperties(botData, botProperties, botMetadata) {
  const languageCode = Object.keys(botProperties.reply_tags)[0];
  const replyTags = botProperties.reply_tags[languageCode];
  const basicProperties = {
    chatflowName: replyTags['%botname'] || botData.title,
    domainName: botData.title,
    description: botData.description,
    testBusiness:
      botData.title ||
      _getBusinessFromViaName(botMetadata, botData.test_business),
    testPartner: _getPartnerFromName(botMetadata, botData.client),
    language: _getLanguageFromCode(botMetadata, languageCode),
  };

  const advancedProperties = {
    botContext: botProperties.bot_context,
    companyContext: botProperties.company_context,
    active: botData.active ? STATUS_ACTIVE : STATUS_INACTIVE,
    gender: replyTags['%botgender'] || 'Male',
    companyName: replyTags['%company'],
    appName: replyTags['%appname'],
    feedbackSmartActionLink: replyTags['%feedbacklink'],
    companyWebsite: replyTags['%sitelink'],
    botLocation: replyTags['%location'],
    botPersona: botProperties.persona || 0,
    contextEnhancementInstr: botProperties.context_enhancement_instr,
    intentDetectionInstr: botProperties.intent_detection_instr,
    answeringInstr: botProperties.answering_instr,
    aiAgentAuthEnabled: botData.ai_agent_auth_enabled,
    detectLanguageForAllMessages: botData.detect_language_for_all_messages,
    aiAgentSessionTimeout: botData.ai_agent_session_timeout,
    aiAgentSessionTimeoutEnabled: botData.ai_agent_session_timeout_enabled,
    addDateAndTimeInPrompt: botData.add_date_and_time_in_prompt,
    aiAgentVoiceEnabled: botData.voice_enabled,
    aIAgentVoiceModel: {
      id: botData.voice_model_id,
      name: botData.voice_model_name,
    },
    aiAgentTtsEnabled: botData.tts_enabled,
    aiAgentPhoneNumber: botData.phone_number,
    aiAgentVoiceForOutput: {
      id: botData.voice_config?.voice_id,
      name: botData.voice_config?.voice_id,
    },
    aiAgentGreetingMessage: botData.voice_config?.greeting_msg,
    aiAgentVoiceProvider: botData.voice_config?.provider,
    aiAgentVoiceLocale: botData.voice_config?.locale,
  };

  return { basicProperties, advancedProperties };
}

export function sendAnalytics(
  testBusiness,
  testPartner,
  updatedBotEnv,
  domainName
) {
  const newPartnerAdded = testPartner.type === 'CREATE';
  const newBusinessAdded = testBusiness.type === 'CREATE';

  let gaLabel = GOOGLE_ANALYTICS.LABEL.OLD_BUSINESS_OLD_PARTNER;
  if (newPartnerAdded && newBusinessAdded) {
    gaLabel = GOOGLE_ANALYTICS.LABEL.NEW_BUSINESS_NEW_PARTNER;
  } else if (newPartnerAdded && !newBusinessAdded) {
    gaLabel = GOOGLE_ANALYTICS.LABEL.OLD_BUSINESS_NEW_PARTNER;
  } else if (!newPartnerAdded && newBusinessAdded) {
    gaLabel = GOOGLE_ANALYTICS.LABEL.NEW_BUSINESS_OLD_PARTNER;
  }

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.BOT,
    GOOGLE_ANALYTICS.EVENTS.BOT_CREATE,
    gaLabel,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  if (newBusinessAdded) {
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.BUSINESS,
      GOOGLE_ANALYTICS.EVENTS.BUSINESS_CREATE_VIA_BOTFLOW,
      testBusiness.viaName,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }

  if (updatedBotEnv.base_url_test !== undefined) {
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.BOT,
      `${GOOGLE_ANALYTICS.EVENTS.BOT_EDIT}-${domainName}`,
      `base-url-test`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }

  if (updatedBotEnv.base_url_production !== undefined) {
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.BOT,
      `${GOOGLE_ANALYTICS.EVENTS.BOT_EDIT}-${domainName}`,
      `base-url-prod`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }
  if (updatedBotEnv.active_env !== undefined) {
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.BOT,
      `${GOOGLE_ANALYTICS.EVENTS.BOT_EDIT}-${domainName}`,
      `active-env`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }
}

function _getBusinessFromViaName(botMetadata, viaName) {
  return botMetadata.businesses.filter(
    (business) => business.viaName === viaName
  )[0];
}

function _getPartnerFromName(botMetadata, partnerName) {
  return botMetadata.partners.filter(
    (partner) => partner.partner_name === partnerName
  )[0];
}

function _getLanguageFromCode(botMetadata, languageCode) {
  return botMetadata.languages.filter(
    (language) => language.code === languageCode
  )[0];
}
